import {ComplectationBlock, Option, OptionsSelected} from "../data";
import {
    stateLoading,
    stateSelectedAccessories,
    stateSelectedComplectations,
    stateAccessoriesBlocks,
    stateComplectationBlocks
} from "../data/atoms";
import {useNotification} from "./useNotification";
import {useScreenshot} from "./useScreenshot";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {useCallback} from "react";
import {
    findBlockItemBySelected,
    findBlockItemWithSlotsBySelected,
    generateUid,
    getBlocksWithoutSlots,
    getBlocksWithSlots,
    splitUid
} from "../utils/ApplicationUtils";
import {Error, ErrorCode} from "../data/types/Error";
import {isDev} from "../utils/EnvironmentUtils";
import {useConstructorSubmitActon} from "./useConstructorSubmitActon";

const groupErrorsByType = (errors: Error[]) => {
    return errors.reduce<{ [key: string]: Error[] }>((total, error) => {
        if (!total.hasOwnProperty(error.code)) {
            total[error.code] = [];
        }

        total[error.code].push(error);

        return total;
    }, {})
};


export const useSubmit = () => {
    const setLoading = useSetRecoilState(stateLoading);

    const {showError, showWarning} = useNotification();
    const {screenshotRef, makeScreenshot} = useScreenshot();

    const complectations = useRecoilValue(stateComplectationBlocks);
    const accessories = useRecoilValue(stateAccessoriesBlocks);

    const selectedComplectations = useRecoilValue(stateSelectedComplectations);
    const selectedAccessories = useRecoilValue(stateSelectedAccessories);

    const submit = useConstructorSubmitActon();

    const validate = useCallback(() => {
        const errorsMutable: Error[] = [];

        validateComplectations(errorsMutable, complectations, selectedComplectations);
        validateAccessories(errorsMutable, accessories, selectedAccessories);

        if (errorsMutable.length) {
            const errors = groupErrorsByType(errorsMutable);

            if (errors[ErrorCode.REQUIRED_NOT_SELECTED]?.length) {
                showWarning(
                    'Не выбраны обязательные элементы',
                    errors[ErrorCode.REQUIRED_NOT_SELECTED].map((error) => {
                        const [blockId, attributeId] = splitUid(error.uid);

                        const block = [...complectations, ...accessories].find(el => el.id === blockId);

                        if (block) {
                            return block.title + ': ' + block.attributes.find(el => el.id === attributeId)?.title || '';
                        }

                        return '';
                    }),
                );
            }

            if (errors[ErrorCode.REQUIRED_MORE_SLOTS]?.length) {
                showWarning(
                    'Нужно выбрать минимум',
                    errors[ErrorCode.REQUIRED_MORE_SLOTS].map((error) => {
                        const [blockId] = splitUid(error.uid);

                        const block = [...complectations, ...accessories].find(el => el.id === blockId);

                        if (block && block.slots) {
                            return block.title + ': ' + block.slots.requiredCount + ' шт.';
                        }

                        return '';
                    }),
                );
            }

            console.error(errorsMutable);

            return false;
        }

        return true;
    }, [accessories, complectations, selectedAccessories, selectedComplectations, showWarning])


    const generateImage = useCallback(async () => {
        let screenshot: string | undefined;

        try {
            screenshot = await makeScreenshot(isDev());
        } catch (err) {
            screenshot = undefined;

            console.error('Image Generation issue: ', err);
        }

        if (!screenshot) {
            showError(
                'Ошибка на сайте!',
                [
                    'При сохранении изображения сумки произошла ошибка, попробуйте ещё раз!',
                    'В случае повторения ошибки, свяжитесь с нами и мы поможем вам оформить заказ.'
                ]
            );
        }

        return screenshot;
    }, [makeScreenshot, showError])


    const onSubmit = useCallback(async () => {
        setLoading(prev => ({...prev, submit: true}));

        const isValid = validate();

        if (!isValid) {
            setLoading(prev => ({...prev, submit: false}));

            return;
        }

        const screenshot = await generateImage();

        if (!screenshot) {
            setLoading(prev => ({...prev, submit: false}));

            return;
        }

        const items = getItems(complectations, accessories, selectedComplectations, selectedAccessories);

        const data = [
            ...items[0].map(item => item.id.toString()), // Complectations
            ...items[1].map(item => item.id.toString()), // Accessories
            ...items[2].map(item => item.id.toString()), // Slots items
        ];

        submit(data, screenshot);

    }, [accessories, complectations, generateImage, selectedAccessories, selectedComplectations, setLoading, submit, validate]);


    return {
        onSubmit,
        screenshotRef,
    }
}


const getItems = (
    complectations: ComplectationBlock[],
    accessories: ComplectationBlock[],
    selectedComplectations: OptionsSelected,
    selectedAccessories: OptionsSelected
) => {

    const itemsComplectations = getBlocksWithoutSlots(complectations)
        .reduce<Option[]>((total, block) => {
            const item = findBlockItemBySelected(block, selectedComplectations);

            if (item) {
                total.push(item);

                if (block.complect) {
                    total.push({id: block.complect.id, price: block.complect.price, attributes: {}});
                }
            }

            return total;
        }, []);

    const itemsAccessories = getBlocksWithoutSlots(accessories)
        .reduce<Option[]>((total, block) => {
            const item = findBlockItemBySelected(block, selectedAccessories);

            if (item) {
                total.push(item);

                if (block.complect) {
                    total.push({id: block.complect.id, price: block.complect.price, attributes: {}});
                }
            }

            return total;
        }, []);


    /**
     * Считаем, что у блоков со слотами нет комплектов
     * Если будут, то потребуется доработать.
     */
    const itemsSlots: Option[] = [];

    itemsSlots.push(...getBlocksWithSlots(complectations)
        .reduce<Option[]>((total, block) => {
            total.push(...findBlockItemWithSlotsBySelected(block, selectedComplectations));

            return total;
        }, [])
    );

    itemsSlots.push(...getBlocksWithSlots(accessories)
        .reduce<Option[]>((total, block) => {
            total.push(...findBlockItemWithSlotsBySelected(block, selectedAccessories));

            return total;
        }, []))

    return [itemsComplectations, itemsAccessories, itemsSlots];

};


const validateComplectations = (
    errorsMutable: Error[],
    complectations: ComplectationBlock[],
    selected: OptionsSelected
) => {
    complectations.forEach((block) => {
        const hasSlots = block.slots && block.slots.positions.length > 0;
        const requiredSlots = block.slots ? block.slots.requiredCount : 0;

        block.attributes.forEach((attribute) => {
            const uid = generateUid(block.id, attribute.id);

            if (!hasSlots) {
                if (!selected.hasOwnProperty(uid) || !selected[uid]?.id) {
                    errorsMutable.push({uid, code: ErrorCode.REQUIRED_NOT_SELECTED});
                }
            } else {
                const selectedSlots = Object.keys(selected).filter(selected => selected.startsWith(uid));

                if (selectedSlots.length === 0) {
                    errorsMutable.push({uid, code: ErrorCode.REQUIRED_NOT_SELECTED});
                } else if (selectedSlots.length < requiredSlots) {
                    errorsMutable.push({uid, code: ErrorCode.REQUIRED_MORE_SLOTS});
                }
            }
        });
    });
};

const validateAccessories = (
    errorsMutable: Error[],
    accessories: ComplectationBlock[],
    selected: OptionsSelected
) => {
    Object.keys(selected).forEach((uid) => {
        const [accessoireId] = splitUid(uid);
        const accessoire = accessories.find(el => el.id === accessoireId);

        if (!accessoire) {
            return;
        }

        const hasSlots = accessoire.slots && accessoire.slots.positions.length > 0;
        const requiredSlots = accessoire.slots ? accessoire.slots.requiredCount || 2 : 0;

        accessoire.attributes.forEach((attribute) => {
            const uid = generateUid(accessoire.id, attribute.id);

            if (!hasSlots) {
                if (!selected.hasOwnProperty(uid) || !selected[uid]?.id) {
                    errorsMutable.push({uid, code: ErrorCode.REQUIRED_NOT_SELECTED});
                }
            } else {
                const selectedSlots = Object.keys(selected).filter(selected => selected.startsWith(uid));

                if (selectedSlots.length === 0) {
                    errorsMutable.push({uid, code: ErrorCode.REQUIRED_NOT_SELECTED});
                } else if (selectedSlots.length < requiredSlots) {
                    errorsMutable.push({uid, code: ErrorCode.REQUIRED_MORE_SLOTS});
                }
            }
        });
    });
}
